import { RefObject } from 'react'

import { SystemStyleObject, chakra } from '@chakra-ui/react'

import { PARAGRAPH_LINE_HEIGHT_PROPORTION } from 'src/components/constants'
import { PARAGRAPH_SPEAKER_NAME_DESKTOP_PADDING } from 'src/components/TranscriptViewer/ParagraphView/constants'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { useUserContext } from 'src/contexts/UserProvider'
import { TranscriptParagraph } from 'src/models/Transcript'
import { isUserWithRoles, User } from 'src/models/User'
import { isChromakeyModeActive, ViewMode } from 'src/utils/env'
import { shouldForwardProp } from 'src/utils/should-forward-prop'

export type ParagraphTextProps = {
    textElementRef: RefObject<HTMLDivElement | null>
    paragraph: TranscriptParagraph
    color: string
    backgroundColor: string
}

export const isGreyOutUnfinalizedAsrActive = (isFeatureEnabled: boolean, user: User | null) =>
    isFeatureEnabled && isUserWithRoles(user, ['court_reporter'])

export const useGreyOutUnfinalizedAsr = () => {
    const isFeatureEnabled = useFeatureFlag('grey_out_unfinalized_asr')
    const { user } = useUserContext()

    return isGreyOutUnfinalizedAsrActive(isFeatureEnabled, user)
}

export const contentEntryPresentation = ({
    viewMode,
    isTranscriptEditAuthorised,
    isEditable,
    greyOutUnfinalizedAsr,
}: {
    viewMode: ViewMode | null | undefined
    isTranscriptEditAuthorised: boolean
    isEditable: boolean
    greyOutUnfinalizedAsr: boolean
}): { isGreyedOut: boolean; isSelectable: boolean } => {
    const isJobView = viewMode === 'job'

    return {
        // Large-screen display mode keeps provisional text in the chosen color.
        isGreyedOut: greyOutUnfinalizedAsr && isJobView && !isEditable && !isChromakeyModeActive,
        isSelectable: !isJobView || !isTranscriptEditAuthorised || isEditable,
    }
}

type ContentEntryProps = { $isGreyedOut: boolean; $isSelectable: boolean }
export const ContentEntry = chakra<'span', ContentEntryProps>('span', {
    baseStyle: ({ $isGreyedOut, $isSelectable }) => ({
        userSelect: $isSelectable ? 'text' : 'none',
        cursor: $isSelectable ? 'auto' : 'wait',
        color: $isGreyedOut ? 'roles.text.disabled' : 'inherit',
    }),
    shouldForwardProp,
})

type ParagraphTextWrapperProps = {
    $color: string
    $backgroundColor: string
    $textTransform?: SystemStyleObject['textTransform']
}
export const ParagraphTextWrapper = chakra<'div', ParagraphTextWrapperProps>('div', {
    baseStyle: ({ $color, $backgroundColor, $textTransform }) => ({
        fontFamily: 'inherit',
        fontSize: '1em',
        color: $color,
        backgroundColor: $backgroundColor,
        width: '100%',
        minHeight: `${PARAGRAPH_LINE_HEIGHT_PROPORTION}em`,
        textTransform: $textTransform,
        padding: isChromakeyModeActive ? `${PARAGRAPH_SPEAKER_NAME_DESKTOP_PADDING}px 0 0` : 0,
        [`& *`]: {
            fontFamily: 'inherit',
        },
    }),
    shouldForwardProp,
})
